import React, { useEffect, useRef, useState } from "react"

/**
 * Lightweight HLS player that uses native playback when available (Safari/iOS),
 * and falls back to hls.js for other browsers.
 */
export default function HlsPlayer({
  src,
  width = "100%",
  height = "auto",
  poster,
  autoPlay = false,
  muted = false,
  controls = true,
  playsInline = true,
  style = {},
  className = "",
}) {
  const videoRef = useRef(null)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient || !videoRef.current || !src) return

    const video = videoRef.current
    let hls

    const canUseNativeHls =
      video.canPlayType("application/vnd.apple.mpegurl") ||
      video.canPlayType("application/x-mpegURL")

    const setup = async () => {
      if (canUseNativeHls) {
        video.src = src
        return
      }
      try {
        const Hls = (await import("hls.js")).default
        if (Hls?.isSupported()) {
          hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
          })
          hls.loadSource(src)
          hls.attachMedia(video)
        } else {
          // Last resort: try setting src directly
          video.src = src
        }
      } catch {
        // Dynamic import failed; try direct assignment
        video.src = src
      }
    }

    setup()

    return () => {
      if (hls) {
        hls.destroy()
      }
    }
  }, [isClient, src])

  return (
    <video
      ref={videoRef}
      style={{ width, height, backgroundColor: "black", ...style }}
      className={className}
      poster={poster}
      controls={controls}
      autoPlay={autoPlay}
      muted={muted}
      playsInline={playsInline}
    />
  )
}

